
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ensureProfileExists } from "@/utils/profileUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

export interface RetailerProfile {
	id: string;
	name: string;
	businessName: string | null;
	website: string | null;
	profile_image: string | null;
	created_at: string;
	updated_at: string;
}

export interface SupplierProfile {
	id: string;
	name: string;
	businessName: string | null;
	accountType: string;
	description: string | null;
	location: string | null;
	profileImage: string | null;
	minimumFreeShipping: number;
	shippingCost: number | null;
	shippingCostType: string;
	minimumOrder: number;
	minDeliveryDays: number | null;
	maxDeliveryDays: number | null;
}

export const useRetailer = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [isUpdating, setIsUpdating] = useState(false);

	// Fetch retailer profile
	const fetchRetailerProfile = async (): Promise<RetailerProfile | null> => {
		if (!user) return null;

		const { data, error } = await supabase
			.from("profiles")
			.select("*")
			.eq("id", user.id)
			.eq("account_type", "retailer")
			.single();

		if (error) {
			if (error.code !== "PGRST116") {
				// PGRST116 is "no rows returned" error
				console.error("Error fetching retailer profile:", error);
				throw error;
			}
			return null;
		}

		return {
			id: data.id,
			name: data.name,
			businessName: data.business_name,
			website: data.website,
			profile_image: data.profile_image,
			created_at: data.created_at,
			updated_at: data.updated_at
		} as RetailerProfile;
	};

	// Update retailer profile
	const updateRetailerProfile = async ({
		name,
		businessName,
		website,
	}: {
		name: string;
		businessName?: string;
		website?: string;
	}) => {
		if (!user) throw new Error("User not authenticated");

		setIsUpdating(true);
		try {
			const { data, error } = await supabase
				.from("profiles")
				.upsert({
					id: user.id,
					name,
					business_name: businessName || null,
					website: website || null,
					account_type: "retailer",
					updated_at: new Date().toISOString(),
				})
				.select()
				.single();

			if (error) throw error;
			return data;
		} catch (error) {
			console.error("Error updating retailer profile:", error);
			toast({
				title: "Error updating profile",
				description: error.message,
				variant: "destructive",
			});
			throw error;
		} finally {
			setIsUpdating(false);
		}
	};

	// Upload profile image
	const uploadProfileImage = async (file: File) => {
		if (!user) throw new Error("User not authenticated");

		setIsUpdating(true);
		try {
			// Use the utility function to ensure profile exists
			const profileCreated = await ensureProfileExists(user.id, "retailer");

			if (!profileCreated) {
				throw new Error("Failed to create or find retailer profile");
			}

			// Upload the file to storage
			const fileExt = file.name.split(".").pop();
			const fileName = `${user.id}/${Date.now()}.${fileExt}`;
			const filePath = fileName;

			const { error: uploadError } = await supabase.storage
				.from("profile_images")
				.upload(filePath, file, {
					upsert: true,
				});

			if (uploadError) throw uploadError;

			// Get the public URL
			const { data: publicUrlData } = supabase.storage
				.from("profile_images")
				.getPublicUrl(filePath);

			// Update profile record with the image URL
			const { error: updateError } = await supabase
				.from("profiles")
				.update({
					profile_image: publicUrlData.publicUrl,
					updated_at: new Date().toISOString(),
				})
				.eq("id", user.id);

			if (updateError) throw updateError;

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({ queryKey: ["retailerProfile"] });

			return publicUrlData.publicUrl;
		} catch (error) {
			console.error("Error uploading profile image:", error);
			toast({
				title: "Error uploading image",
				description: error.message,
				variant: "destructive",
			});
			throw error;
		} finally {
			setIsUpdating(false);
		}
	};

	// Use React Query to manage retailer profile data
	const {
		data: retailerProfile,
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: ["retailerProfile", user?.id],
		queryFn: fetchRetailerProfile,
		enabled: !!user,
	});

	// Mutation for updating profile
	const updateProfileMutation = useMutation({
		mutationFn: updateRetailerProfile,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["retailerProfile"] });
			toast({
				title: "Profile updated",
				description:
					"Your retailer profile has been successfully updated.",
			});
		},
	});

	// Mutation for uploading profile image
	const uploadImageMutation = useMutation({
		mutationFn: uploadProfileImage,
		onSuccess: () => {
			toast({
				title: "Image uploaded",
				description:
					"Your profile image has been successfully updated.",
			});
		},
	});

	return {
		retailerProfile,
		isLoading,
		error,
		refetch,
		updateProfile: updateProfileMutation.mutate,
		uploadProfileImage: uploadImageMutation.mutate,
		isUpdating:
			isUpdating ||
			updateProfileMutation.isPending ||
			uploadImageMutation.isPending,
	};
};
